import { DraftPermissionRow, DraftPermissionRowMap } from "@manageContacts/models";
import { ManageContactsEditState } from "./models";

/**
 * sessionStorage key for the Manage Contacts edit-mode state. Versioned so a future
 * shape change can bump the key and discard the older payload instead of trying to
 * migrate stale snapshots (the cost of accidentally restoring a malformed draft is
 * higher than the cost of asking the user to re-enter their edits).
 *
 * sessionStorage (not localStorage) is intentional: edits are tied to the current
 * browser tab / session so a fresh tab or a different user on the same machine
 * never sees another user's pending changes.
 */
export const MANAGE_CONTACTS_EDIT_STORAGE_KEY = "lpc.manageContacts.edit.v2";

export const emptyEditState: ManageContactsEditState = {
  isEditMode: false,
  originalRowMap: {},
  draftRowMap: {},
  removedRowIds: [],
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isDraftPermissionRow = (value: unknown): value is DraftPermissionRow => {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;

  return isStringArray(row.roles) && isStringArray(row.documentAccess) && isStringArray(row.notifications);
};

const isDraftPermissionRowMap = (value: unknown): value is DraftPermissionRowMap => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  return Object.values(value as Record<string, unknown>).every(isDraftPermissionRow);
};

const isManageContactsEditState = (value: unknown): value is ManageContactsEditState => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.isEditMode === "boolean" &&
    isDraftPermissionRowMap(candidate.originalRowMap) &&
    isDraftPermissionRowMap(candidate.draftRowMap) &&
    isStringArray(candidate.removedRowIds)
  );
};

const getStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    // sessionStorage access can throw in some sandboxed contexts (e.g. cross-origin iframes
    // or strict privacy modes). Treat as "no storage available".
    return null;
  }
};

/**
 * Read the persisted edit state from sessionStorage and validate its shape. Returns
 * `emptyEditState` on any failure (missing key, malformed JSON, shape mismatch, or
 * storage access error). The validator is strict on purpose — restoring partial /
 * malformed state into the slice would silently break edit mode without an error.
 */
export const loadEditStateFromStorage = (): ManageContactsEditState => {
  const storage = getStorage();

  if (!storage) return emptyEditState;
  try {
    const raw = storage.getItem(MANAGE_CONTACTS_EDIT_STORAGE_KEY);

    if (!raw) return emptyEditState;
    const parsed: unknown = JSON.parse(raw);

    if (!isManageContactsEditState(parsed)) return emptyEditState;
    // Only restore if there is actual edit content — restoring `isEditMode:false` with
    // empty maps is equivalent to no persisted state and avoids re-rendering edit-mode
    // UI just because the user previously discarded.
    if (!parsed.isEditMode && Object.keys(parsed.draftRowMap).length === 0 && parsed.removedRowIds.length === 0) {
      return emptyEditState;
    }

    return parsed;
  } catch {
    return emptyEditState;
  }
};

export const saveEditStateToStorage = (state: ManageContactsEditState): void => {
  const storage = getStorage();

  if (!storage) return;
  try {
    storage.setItem(MANAGE_CONTACTS_EDIT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota errors and serialisation errors are swallowed — losing the persisted
    // snapshot degrades to "Redux-only persistence" which is still better than
    // crashing the edit flow.
  }
};

export const clearEditStateFromStorage = (): void => {
  const storage = getStorage();

  if (!storage) return;
  try {
    storage.removeItem(MANAGE_CONTACTS_EDIT_STORAGE_KEY);
  } catch {
    // ignore — see saveEditStateToStorage
  }
};
